import { useMemo } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';

import type { CreateWebsiteCategoryPageRequest } from '@utazzo/shared';

import { OrderedPickerList } from '@/components/ordered-picker/OrderedPickerList';
import { useDestinationPagesList } from '@/features/destination-pages/use-destination-pages';

export const CircleDestinationsEditor = ({
  form,
  disabled,
}: {
  form: UseFormReturn<CreateWebsiteCategoryPageRequest>;
  disabled?: boolean;
}): React.JSX.Element => {
  const circleDestinations = useWatch({ control: form.control, name: 'circleDestinations' }) ?? [];

  const destinationsQuery = useDestinationPagesList({
    page: 1,
    pageSize: 200,
    sortBy: 'updatedAt',
    sortDir: 'desc',
    status: 'PUBLISHED',
  });

  const options = useMemo(
    () =>
      (destinationsQuery.data?.data ?? []).map((d) => ({
        id: d.id,
        label: d.title,
        description: d.slug,
      })),
    [destinationsQuery.data],
  );

  const selectedIds = useMemo(
    () => circleDestinations.map((c) => c.destinationPageId).filter(Boolean),
    [circleDestinations],
  );

  const syncCircles = (ids: string[]): void => {
    form.setValue(
      'circleDestinations',
      ids.map((destinationPageId) => ({ destinationPageId })),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">
        Circular destination quick-picks under the hero on /community-trips. Leave empty to
        auto-list every linked destination page. Circles need a tile/hero image and a destination
        master binding to appear on the site.
      </p>
      <OrderedPickerList
        options={options}
        value={selectedIds}
        onChange={syncCircles}
        loading={destinationsQuery.isLoading}
        disabled={disabled}
        maxSelected={24}
        pickerLabel="Destination pages"
        pickerHint="Select destinations to feature, then drag to set circle order."
        searchPlaceholder="Search destination pages…"
        sortLabel="Circle order"
        sortEmptyMessage="No destinations selected. Pick destination pages from the list above."
      />
    </div>
  );
};
